<!--
  SupportMessageThread.svelte — a ticket's conversation, oldest first.

  Three kinds of entry share the column. Replies are the conversation itself.
  Internal notes are replies only agents can see, so they must never be mistaken
  for one: they carry an amber tint, a lock and an "Internal note" label, which
  is what stops an agent quoting a note back to the customer thinking it was
  sent. Events (status changes, assignment, automatic resolution) are the
  ticket's own history and render as a single muted line between messages,
  so they mark time without competing with what people wrote.

  Bodies are Markdown, rendered through `SupportMarkdown` — raw HTML in them is
  escaped, links are vetted, and the result is sanitized before it is inserted.
-->
<script lang="ts">
	import { Spinner } from 'flowbite-svelte';
	import RelativeTime from './RelativeTime.svelte';
	import SupportMarkdown from './SupportMarkdown.svelte';
	import SupportImageAttachment from './SupportImageAttachment.svelte';
	import { downloadSupportAttachment } from '../AppNetwork';
	import type { SupportAttachment, SupportMessage, SupportPerson } from '../AppTypes';
	import {
		describeSupportEvent,
		formatFileSize,
		isPreviewableImage,
		resolveSupportAvatarURL,
		type SupportAudience
	} from '../support/supportFormat';
	import { toastError } from '../toast';
	import site from '../site';
	import { t, t2 } from '../i18n';

	interface Props {
		/** The thread, oldest first. */
		messages: SupportMessage[];
		/** Which side of the desk is reading it — decides how statuses in events are named. */
		audience?: SupportAudience;
	}

	let { messages, audience = 'customer' }: Props = $props();

	/** Attachments whose download is in flight, so each shows its own spinner. */
	let downloading: string[] = $state([]);

	/** Downloads one attachment through the authenticated fetch, then saves it. */
	async function download(attachment: SupportAttachment): Promise<void> {
		downloading = [...downloading, attachment.attachmentUUID];
		/** The request carries the JWT as a header; a plain link would arrive unauthenticated. */
		try {
			await downloadSupportAttachment(attachment);
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to download the attachment'));
		} finally {
			downloading = downloading.filter((uuid) => uuid !== attachment.attachmentUUID);
		}
	}

	/** Up to two initials, for an author with no avatar. */
	function initials(person: SupportPerson | null): string {
		const parts = (person?.name ?? '').trim().split(/\s+/).filter(Boolean);
		return parts
			.slice(0, 2)
			.map((part) => part[0].toUpperCase())
			.join('');
	}

	/** The name an entry is attributed to — the system when nobody wrote it. */
	function authorName(message: SupportMessage): string {
		return message.author?.name ?? t2('System');
	}
</script>

<ol class="space-y-4" aria-label={$t('Conversation')}>
	{#each messages as message (message.messageUUID)}
		{#if message.kind === 'event'}
			<li class="text-default flex items-center justify-center gap-2 text-center text-xs" data-kind="event">
				<i class="fa-solid fa-clock-rotate-left opacity-60"></i>
				<span>
					{#if message.author}<span class="font-medium">{message.author.name}</span>&nbsp;·
					{/if}{describeSupportEvent(message, $t, audience)} ·
					<RelativeTime date={message.created} />
				</span>
			</li>
		{:else}
			{@const isNote = message.kind === 'note'}
			{@const avatar = resolveSupportAvatarURL(message.author?.avatarURL, site.backend.baseURL)}
			<li
				data-kind={message.kind}
				class="rounded-2xl p-4 {isNote
					? 'bg-amber-50 text-amber-950 dark:bg-amber-900/20 dark:text-amber-100'
					: 'bg-depth'}"
			>
				<div class="mb-2 flex flex-wrap items-center gap-2">
					{#if avatar}
						<img src={avatar} alt="" class="h-8 w-8 shrink-0 rounded-full object-cover" />
					{:else}
						<div
							class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold {message.authorRole ===
							'agent'
								? 'bg-primary-100 text-primary-700 dark:bg-primary-600/20 dark:text-primary-300'
								: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}"
							aria-hidden="true"
						>
							{initials(message.author)}
						</div>
					{/if}
					<span class="font-semibold">{authorName(message)}</span>
					{#if message.authorRole === 'agent'}
						<span class="text-primary-600 dark:text-primary-400 text-xs font-medium">
							<i class="fa-solid fa-headset me-1"></i>{$t('Support')}
						</span>
					{/if}
					{#if isNote}
						<span
							class="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-500/30 dark:text-amber-100"
						>
							<i class="fa-solid fa-lock me-1"></i>{$t('Internal note')}
						</span>
					{/if}
					<span class="text-default ms-auto text-xs"><RelativeTime date={message.created} /></span>
				</div>

				{#if message.body}
					<SupportMarkdown text={message.body} class="text-sm leading-relaxed" />
				{/if}

				{#if message.attachments.length > 0}
					{@const images = message.attachments.filter((attachment) => isPreviewableImage(attachment.contentType))}
					{@const files = message.attachments.filter((attachment) => !isPreviewableImage(attachment.contentType))}
					<!-- Images fill the message's width — one alone at 16:9, two side by
					     side, more in a grid that never leaves a column standing empty — and
					     other files follow as a row of chips: mixing the two in one row sat
					     small chips at the foot of tall tiles. -->
					{#if images.length > 0}
						<ul
							class="mt-3 grid gap-2 {images.length === 1
								? 'grid-cols-1'
								: images.length === 2
									? 'grid-cols-2'
									: 'grid-cols-2 xl:grid-cols-3'}"
							aria-label={$t('Images')}
						>
							{#each images as attachment (attachment.attachmentUUID)}
								<li class="min-w-0">
									<SupportImageAttachment {attachment} ondownload={download} wide={images.length === 1} />
								</li>
							{/each}
						</ul>
					{/if}
					{#if files.length > 0}
						<ul class="mt-3 flex flex-wrap gap-2" aria-label={$t('Attachments')}>
							{#each files as attachment (attachment.attachmentUUID)}
								<li>
									<button
										type="button"
										class="bg-depth depth-hover inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs"
										disabled={downloading.includes(attachment.attachmentUUID)}
										onclick={() => download(attachment)}
										title={$t('Download {{name}}', { name: attachment.filename })}
									>
										{#if downloading.includes(attachment.attachmentUUID)}
											<Spinner size="4" />
										{:else}
											<i class="fa-solid fa-paperclip"></i>
										{/if}
										<span class="font-medium break-all">{attachment.filename}</span>
										<span class="text-default">{formatFileSize(attachment.size)}</span>
									</button>
								</li>
							{/each}
						</ul>
					{/if}
				{/if}
			</li>
		{/if}
	{/each}
</ol>
